import { createContext, useCallback, useReducer } from "react";
import axios from "../../utils/axios/v1/userAxios";

const GET_ROLES = "GET_ROLES";
const GET_ROLE = "GET_ROLE";
const GET_PERMISSIONS = "GET_PERMISSIONS";
const GET_PROJECT_MEMBERS = "GET_PROJECT_MEMBERS";
const GET_MY_PROJECTS = "GET_MY_PROJECTS";
const GET_ACCESSIBLE_PROJECTS = "GET_ACCESSIBLE_PROJECTS";
const GET_PROJECT_PERMISSIONS = "GET_PROJECT_PERMISSIONS";
const SET_LOADING = "SET_LOADING";
const SET_ERROR = "SET_ERROR";
const CLEAR_ERROR = "CLEAR_ERROR";

const initialState = {
  roles: [],
  currentRole: null,
  availablePermissions: [],
  projectMembers: [],
  myProjects: [],
  accessibleProjects: [],
  projectPermissions: [],
  loading: false,
  error: null,
};

const RBACReducer = (state, action) => {
  switch (action.type) {
    case GET_ROLES:
      return { ...state, roles: action.payload.roles, loading: false };
    case GET_ROLE:
      return { ...state, currentRole: action.payload.role, loading: false };
    case GET_PERMISSIONS:
      return { ...state, availablePermissions: action.payload.permissions, loading: false };
    case GET_PROJECT_MEMBERS:
      return { ...state, projectMembers: action.payload.members, loading: false };
    case GET_MY_PROJECTS:
      return { ...state, myProjects: action.payload.projects, loading: false };
    case GET_ACCESSIBLE_PROJECTS:
      return { ...state, accessibleProjects: action.payload.projects, loading: false };
    case GET_PROJECT_PERMISSIONS:
      return { ...state, projectPermissions: action.payload.permissions, loading: false };
    case SET_LOADING:
      return { ...state, loading: action.payload.loading };
    case SET_ERROR:
      return { ...state, error: action.payload.error, loading: false };
    case CLEAR_ERROR:
      return { ...state, error: null };
    default:
      return state;
  }
};

const RBACContext = createContext(null);

function RBACProvider({ children }) {
  const [state, dispatch] = useReducer(RBACReducer, initialState);

  const setLoading = useCallback((loading) => {
    dispatch({ type: SET_LOADING, payload: { loading } });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: SET_ERROR, payload: { error } });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: CLEAR_ERROR });
  }, []);

  const getRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("/roles");
      dispatch({ type: GET_ROLES, payload: { roles: response.data?.roles || response.data || [] } });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.status === 404 
        ? "Roles API not available. This feature requires backend support."
        : err.message || "Failed to fetch roles";
      setError(errorMessage);
      dispatch({ type: GET_ROLES, payload: { roles: [] } });
      throw err;
    }
  }, [setLoading, setError]);

  const getRole = useCallback(async (roleId) => {
    setLoading(true);
    try {
      const response = await axios.get(`/roles/${roleId}`);
      dispatch({ type: GET_ROLE, payload: { role: response.data?.role || response.data } });
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to fetch role");
      throw err;
    }
  }, [setLoading, setError]);

  const getAvailablePermissions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("/roles/permissions");
      dispatch({ type: GET_PERMISSIONS, payload: { permissions: response.data?.permissions || response.data || [] } });
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to fetch permissions");
      throw err;
    }
  }, [setLoading, setError]);

  const createRole = useCallback(async (roleData) => {
    setLoading(true);
    try {
      const response = await axios.post("/roles", roleData);
      await getRoles();
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to create role");
      throw err;
    }
  }, [setLoading, setError, getRoles]);

  const cloneRole = useCallback(async (roleId, name) => {
    setLoading(true);
    try {
      const response = await axios.post(`/roles/${roleId}/clone`, { name });
      await getRoles();
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to clone role");
      throw err;
    }
  }, [setLoading, setError, getRoles]);

  const updateRole = useCallback(async (roleId, roleData) => {
    setLoading(true);
    try {
      const response = await axios.put(`/roles/${roleId}`, roleData);
      await getRoles();
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to update role");
      throw err;
    }
  }, [setLoading, setError, getRoles]);

  const deleteRole = useCallback(async (roleId) => {
    setLoading(true);
    try {
      const response = await axios.delete(`/roles/${roleId}`);
      await getRoles();
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to delete role");
      throw err;
    }
  }, [setLoading, setError, getRoles]);

  const getMyProjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get("/project-members/my-projects");
      dispatch({ type: GET_MY_PROJECTS, payload: { projects: response.data?.projects || response.data || [] } });
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to fetch my projects");
      throw err;
    }
  }, [setLoading, setError]);

  const getAccessibleProjects = useCallback(async (permission = null) => {
    setLoading(true);
    try {
      const url = permission ? `/project-members/accessible?permission=${permission}` : "/project-members/accessible";
      const response = await axios.get(url);
      dispatch({ type: GET_ACCESSIBLE_PROJECTS, payload: { projects: response.data?.projects || response.data || [] } });
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to fetch accessible projects");
      throw err;
    }
  }, [setLoading, setError]);

  const getProjectPermissions = useCallback(async (projectId) => {
    setLoading(true);
    try {
      const response = await axios.get(`/project-members/${projectId}/permissions`);
      const permissions = response.data?.permissions || response.data || [];
      dispatch({ type: GET_PROJECT_PERMISSIONS, payload: { permissions } });
      return permissions;
    } catch (err) {
      setError(err.message || "Failed to fetch project permissions");
      throw err;
    }
  }, [setLoading, setError]);

  const getProjectMembers = useCallback(async (projectId) => {
    setLoading(true);
    try {
      const response = await axios.get(`/project-members/${projectId}`);
      dispatch({ type: GET_PROJECT_MEMBERS, payload: { members: response.data?.members || response.data || [] } });
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to fetch project members");
      throw err;
    }
  }, [setLoading, setError]);

  const addProjectMember = useCallback(async (projectId, userId, roleId) => {
    setLoading(true);
    try {
      const response = await axios.post(`/project-members/${projectId}`, { user_id: userId, role_id: roleId });
      await getProjectMembers(projectId);
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to add project member");
      throw err;
    }
  }, [setLoading, setError, getProjectMembers]);

  const bulkAddProjectMembers = useCallback(async (projectId, members) => {
    setLoading(true);
    try {
      const response = await axios.post(`/project-members/${projectId}/bulk`, { members });
      await getProjectMembers(projectId);
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to bulk add members");
      throw err;
    }
  }, [setLoading, setError, getProjectMembers]);

  const updateProjectMemberRole = useCallback(async (projectId, membershipId, roleId) => {
    setLoading(true);
    try {
      const response = await axios.put(`/project-members/${projectId}/${membershipId}`, { role_id: roleId });
      await getProjectMembers(projectId);
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to update member role");
      throw err;
    }
  }, [setLoading, setError, getProjectMembers]);

  const removeProjectMember = useCallback(async (projectId, membershipId) => {
    setLoading(true);
    try {
      const response = await axios.delete(`/project-members/${projectId}/${membershipId}`);
      await getProjectMembers(projectId);
      return response.data;
    } catch (err) {
      setError(err.message || "Failed to remove member");
      throw err;
    }
  }, [setLoading, setError, getProjectMembers]);

  const hasPermission = useCallback((permission) => {
    return state.projectPermissions.includes(permission);
  }, [state.projectPermissions]);

  const hasAnyPermission = useCallback((permissions) => {
    return permissions.some((p) => state.projectPermissions.includes(p));
  }, [state.projectPermissions]);

  const hasAllPermissions = useCallback((permissions) => {
    return permissions.every((p) => state.projectPermissions.includes(p));
  }, [state.projectPermissions]);

  return (
    <RBACContext.Provider
      value={{
        ...state,
        getRoles,
        getRole,
        getAvailablePermissions,
        createRole,
        cloneRole,
        updateRole,
        deleteRole,
        getMyProjects,
        getAccessibleProjects,
        getProjectPermissions,
        getProjectMembers,
        addProjectMember,
        bulkAddProjectMembers,
        updateProjectMemberRole,
        removeProjectMember,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
        setError,
        clearError,
      }}
    >
      {children}
    </RBACContext.Provider>
  );
}

export { RBACContext, RBACProvider };
